import React from 'react';
import { useSelector } from 'react-redux';
import { Avatar, Dropdown, Flex, type MenuProps } from 'antd';
import {
  AiAssistantHeaderIcon,
  SupportHeaderIcon,
} from '../../assets/icons/icons';
import theme from '../../assets/theme';
import { getInitials } from '../../common/helper';
import { CustomTitle } from '../../components';
import NotificationDropdown from '../../pages/Notifications/NotificationDropdown';
import type { RootState } from '@/redux-toolkit/store';
import { trackEvent } from '@/hooks';
import { BeaconEventType } from '@/common/enum';
import './header.css';

interface CustomHeaderProps {
  decodedToken?: {
    badge_color?: string;
    name?: string;
  } | null;
  helpDropdown?: React.ReactNode;
  helpOpen?: boolean;
  menuItems?: MenuProps['items'];
  onAiInsightsOpen?: () => void;
  onHelpOpenChange?: (open: boolean) => void;
  onOpenHelpCenter?: () => void;
  helpCenterOpen?: boolean;
  onProfileMenuClick?: (e: { key: string }) => void;
  showAiInsightsChat?: boolean;
  showMessages?: boolean;
}

// Help: with onOpenHelpCenter (uat/localhost) the button opens the Help center;
// without it (production) the old Help dropdown shows instead.
const CustomHeader: React.FC<CustomHeaderProps> = ({
  decodedToken = null,
  helpDropdown = null,
  helpOpen = false,
  menuItems = [],
  onAiInsightsOpen = () => {},
  onHelpOpenChange = () => {},
  onOpenHelpCenter,
  helpCenterOpen = false,
  onProfileMenuClick = () => {},
  showAiInsightsChat = false,
  showMessages = false,
}) => {
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );

  return (
    <Flex align='center' justify='space-between' style={{ height: 'inherit' }}>
      <div className='header-logo-wrap'>
        {theme.themeType === 1 ? (
          <CustomTitle
            text={theme.title}
            color='var(--zl-header-title, #020922)'
            fontSize={16}
            fontWeight={600}
            lineHeight='28px'
            marginBottom={0}
          />
        ) : darkModeEnabled ? (
          <img
            src={theme.whiteLogo}
            width={theme.headerLogoWidth}
            className='header-logo'
            // eslint-disable-next-line react/forbid-dom-props -- logo filter comes from the active theme at run time
            style={
              theme.whiteLogoFilter
                ? { filter: theme.whiteLogoFilter }
                : undefined
            }
            alt=''
          />
        ) : (
          <img
            src={theme.headerLogo}
            width={theme.headerLogoWidth}
            className='header-logo'
            alt=''
          />
        )}
      </div>

      <Flex gap={12} align='center'>
        {showAiInsightsChat && (
          <button
            type='button'
            aria-label='Open AI Insights'
            onClick={onAiInsightsOpen}
            className='aiInsightsButton'
          >
            <AiAssistantHeaderIcon width={38} height={38} />
          </button>
        )}

        {onOpenHelpCenter ? (
          <button
            type='button'
            aria-label='Open Help'
            aria-pressed={helpCenterOpen}
            onClick={onOpenHelpCenter}
            className='helpCenterButton'
          >
            <Avatar
              size={36}
              icon={
                <SupportHeaderIcon
                  width={18}
                  height={18}
                  color='var(--text-primary)'
                />
              }
              style={{
                cursor: 'pointer',
                backgroundColor: 'var(--white)',
                // PRD: "Active state: 1.5px black border on the round icon button."
                border: helpCenterOpen
                  ? '1.5px solid var(--text-primary)'
                  : '1.5px solid transparent',
              }}
            />
          </button>
        ) : (
          // Old Help dropdown (tour / chat / email), kept for production.
          <Dropdown
            trigger={['click']}
            placement='bottomRight'
            dropdownRender={() => helpDropdown}
            overlayStyle={{ zIndex: 2100 }}
            open={helpOpen}
            onOpenChange={onHelpOpenChange}
          >
            <span>
              <Avatar
                size={36}
                icon={<SupportHeaderIcon width={18} height={18} />}
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--gray-300)',
                }}
              />
            </span>
          </Dropdown>
        )}

        {showMessages && (
          <NotificationDropdown
            overlayStyle={{ zIndex: 2100 }}
            onOpenChange={(open) => {
              if (open) trackEvent(BeaconEventType.MessagesOpened);
            }}
          />
        )}

        <Dropdown
          menu={{ items: menuItems, onClick: onProfileMenuClick }}
          trigger={['click']}
          placement='bottomRight'
          overlayStyle={{ zIndex: 2100 }}
        >
          <div
            className='initials-avatar cursor-pointer'
            // eslint-disable-next-line react/forbid-dom-props -- badge colour comes from the user's token at run time
            style={{
              backgroundColor: decodedToken?.badge_color || '#000',
            }}
          >
            {getInitials(decodedToken?.name || '')}
          </div>
        </Dropdown>
      </Flex>
    </Flex>
  );
};

export default CustomHeader;
